import { Button, DatePicker, Radio, message } from '@maxtropy/components';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import styles from './index.module.scss';

export type Granularity = 'day' | 'month' | 'year';

export interface AnalysisFilterValue {
  granularity: Granularity;
  range: [Dayjs, Dayjs];
}

interface AnalysisToolbarProps {
  showGranularity?: boolean;
  enforceRangeRules?: boolean;
  onChange?: (value: AnalysisFilterValue) => void;
  onExport?: (value: AnalysisFilterValue) => void;
}

const { RangePicker } = DatePicker;
type RangeValue = [Dayjs | null, Dayjs | null] | null;

const rangeRuleMap = {
  day: { unit: 'day', maxSpan: 31, recentOffset: 30 },
  month: { unit: 'month', maxSpan: 36, recentOffset: 11 },
  year: { unit: 'year', maxSpan: 6, recentOffset: 5 },
} as const;

const getRecentRange = (granularity: Granularity): [Dayjs, Dayjs] => {
  const rule = rangeRuleMap[granularity];
  const end = dayjs().startOf(granularity);
  return [end.subtract(rule.recentOffset, rule.unit), end];
};

const AnalysisToolbar = ({
  showGranularity = true,
  enforceRangeRules = false,
  onChange,
  onExport,
}: AnalysisToolbarProps) => {
  const [granularity, setGranularity] = useState<Granularity>('month');
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() =>
    enforceRangeRules ? getRecentRange('month') : [dayjs('2026-12-01'), dayjs('2027-12-01')]
  );
  const [panelRange, setPanelRange] = useState<RangeValue>(null);
  const picker = granularity === 'day' ? 'date' : granularity;
  const format = granularity === 'day' ? 'YYYY-MM-DD' : granularity === 'month' ? 'YYYY-MM' : 'YYYY';
  const rangeRule = rangeRuleMap[granularity];

  useEffect(() => {
    onChange?.({ granularity, range });
  }, [granularity, onChange, range]);

  const handleGranularityChange = (nextGranularity: Granularity) => {
    setGranularity(nextGranularity);
    setPanelRange(null);
    if (enforceRangeRules) setRange(getRecentRange(nextGranularity));
  };

  const handleRangeChange = (value: [Dayjs, Dayjs] | null) => {
    if (!value) return;

    if (enforceRangeRules && value[1].diff(value[0], rangeRule.unit, true) >= rangeRule.maxSpan) {
      return;
    }

    setRange(value);
  };

  const disabledDate = (current: Dayjs) => {
    if (!enforceRangeRules || !panelRange) return false;

    const tooLate = panelRange[0] && current.diff(panelRange[0], rangeRule.unit) >= rangeRule.maxSpan;
    const tooEarly = panelRange[1] && panelRange[1].diff(current, rangeRule.unit) >= rangeRule.maxSpan;
    return Boolean(tooEarly || tooLate);
  };

  return (
    <div className={styles.toolbar}>
      <div className={styles.filters}>
        {showGranularity && (
          <Radio.Group
            value={granularity}
            buttonStyle="solid"
            onChange={event => handleGranularityChange(event.target.value)}
          >
            <Radio.Button value="day">按日</Radio.Button>
            <Radio.Button value="month">按月</Radio.Button>
            <Radio.Button value="year">按年</Radio.Button>
          </Radio.Group>
        )}
        <RangePicker
          picker={picker}
          value={panelRange ?? range}
          format={format}
          allowClear={false}
          disabledDate={disabledDate}
          onCalendarChange={value => setPanelRange(value as RangeValue)}
          onOpenChange={open => setPanelRange(open ? [null, null] : null)}
          onChange={value => handleRangeChange(value as [Dayjs, Dayjs] | null)}
        />
      </div>
      <Button
        type="primary"
        onClick={() => (onExport ? onExport({ granularity, range }) : message.success('导出任务已创建'))}
      >
        导出
      </Button>
    </div>
  );
};

export default AnalysisToolbar;
